import { MagnifyingGlassIcon } from "@heroicons/react/24/outline";

function formatBarcode(value) {
    if (value == null || value === "" || value === "-") return "-";
    return String(value).replace(/\.0$/, "");
}

function TypeBadge({ type }) {
    if (type === "FBA") {
        return (
            <span
                className="inline-flex items-center rounded px-2 py-0.5 text-xs font-medium"
                style={{ background: "#E8F0FE", color: "oklch(0.30 0.14 220)" }}
            >
                FBA
            </span>
        );
    }

    return (
        <span
            className="inline-flex items-center rounded px-2 py-0.5 text-xs font-medium"
            style={{ background: "oklch(0.94 0.06 280 / 0.3)", color: "oklch(0.35 0.12 280)" }}
        >
            FBM
        </span>
    );
}

function StatusBadge({ status }) {
    const normalized = String(status || "").toUpperCase();

    if (normalized === "ACTIVE") {
        return (
            <span
                className="inline-flex items-center rounded px-2 py-0.5 text-xs font-medium"
                style={{ background: "oklch(0.95 0.06 145)", color: "oklch(0.35 0.14 145)" }}
            >
                Active
            </span>
        );
    }

    if (normalized === "IN_ACTIVE") {
        return (
            <span
                className="inline-flex items-center rounded px-2 py-0.5 text-xs font-medium"
                style={{ background: "oklch(0.96 0.002 260)", color: "oklch(0.50 0.01 260)" }}
            >
                Inactive
            </span>
        );
    }

    if (normalized === "SUNSET") {
        return (
            <span className="inline-flex items-center rounded px-2 py-0.5 text-xs font-medium bg-amber-100 text-amber-900">
                Sunset
            </span>
        );
    }

    if (normalized === "PLANNED") {
        return (
            <span className="inline-flex items-center rounded px-2 py-0.5 text-xs font-medium bg-blue-100 text-blue-800">
                Planned
            </span>
        );
    }

    return <span className="inline-flex items-center rounded px-2 py-0.5 text-xs font-medium bg-gray-100 text-gray-600">{status || "-"}</span>;
}

export default function AmazonMappingTable({ rows, loading }) {
    const thClass = "px-3 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-gray-600 border-b border-gray-200 whitespace-nowrap";
    const tdClass = "px-3 py-2 text-xs text-gray-900 border-b border-gray-100 align-middle";

    if (loading && rows.length === 0) {
        return (
            <div className="flex items-center justify-center px-4 py-12 text-sm text-gray-500">
                Loading Amazon mappings...
            </div>
        );
    }

    return (
        <div className="flex h-full flex-col">
            <div className="flex-1 overflow-auto border-x border-b border-gray-200 bg-white">
                <table className="min-w-[960px] w-full border-collapse text-sm">
                    <thead className="sticky top-0 z-10 bg-white">
                        <tr>
                            <th className={thClass}>Execution SKU</th>
                            <th className={thClass}>Product</th>
                            <th className={`${thClass} w-20`}>Type</th>
                            <th className={thClass}>Supply SKU</th>
                            <th className={`${thClass} w-32`}>Barcode</th>
                            <th className={`${thClass} w-24`}>Status</th>
                            <th className={`${thClass} w-24`}>Forecastable</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.length === 0 ? (
                            <tr>
                                <td colSpan={7} className="px-3 py-12 text-center">
                                    <div className="flex flex-col items-center justify-center text-center">
                                        <MagnifyingGlassIcon className="mb-3 h-8 w-8 text-gray-300" aria-hidden />
                                        <p className="text-sm font-medium text-gray-500">No rows match your filters</p>
                                    </div>
                                </td>
                            </tr>
                        ) : (
                            rows.map((row, index) => (
                                <tr
                                    key={row.id}
                                    className={index % 2 === 0 ? "bg-white" : "bg-gray-50/60"}
                                >
                                    <td className={`${tdClass} font-medium`}>{row.execution_sku || "-"}</td>
                                    <td className={`${tdClass} max-w-[260px]`}>
                                        <span className="block truncate text-gray-500">{row.canonical_product_name || "-"}</span>
                                    </td>
                                    <td className={tdClass}>
                                        <TypeBadge type={row.type} />
                                    </td>
                                    <td className={tdClass}>
                                        <span className="inline-flex items-center rounded border border-gray-200 bg-white px-[10px] py-0.5 text-xs font-medium text-gray-900">
                                            {row.supply_sku || "-"}
                                        </span>
                                    </td>
                                    <td className={tdClass}>
                                        <span>{formatBarcode(row.barcode)}</span>
                                    </td>
                                    <td className={tdClass}>
                                        <StatusBadge status={row.status} />
                                    </td>
                                    <td className={tdClass}>
                                        <span className="text-gray-500">{row.forecastable ? "Yes" : "No"}</span>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            <div className="border border-t-0 border-gray-200 bg-gray-50 px-4 py-2">
                <p className="text-xs text-gray-500">
                    {rows.length.toLocaleString()} rows · Amazon SKU Mapping · Read-only
                </p>
            </div>
        </div>
    );
}
